/**
 * Pure row operations for the Surge/Early-Bird builders.
 * Extracted so they can be unit-tested without React/final-form, and so
 * add/remove always operate on a coerced array (never a stale/leaf value).
 */

import { DEFAULT_TIME_THRESHOLD_UNIT } from "../constants";

/**
 * VIP time pickers (batch-creation ExlyTimePicker) store 12h strings
 * ("02:30 pm"); the backend contract is "HH:MM". Normalize at the payload
 * boundary (submit) and back on prefill (edit) so the UI and backend never
 * drift. Already-24h values pass through untouched.
 */
export const normalizeClockToHHMM = (value) => {
  if (!value || typeof value !== "string") return value;
  const m12 = value.trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp])\.?[Mm]?\.?$/);
  if (m12) {
    let h = parseInt(m12[1], 10) % 12;
    if (/^[Pp]/.test(m12[3])) h += 12;
    return `${String(h).padStart(2, "0")}:${m12[2]}`;
  }
  const m24 = value.trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (m24) {
    return `${m24[1].padStart(2, "0")}:${m24[2]}`;
  }
  return value;
};

export const normalizeClockTo12h = (value) => {
  if (!value || typeof value !== "string") return value;
  const m = value.trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (!m) return value;
  let h = parseInt(m[1], 10);
  const suffix = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${String(h).padStart(2, "0")}:${m[2]} ${suffix}`;
};

/**
 * Coerce anything into a row array. Handles the final-form edge case where a
 * removed-nested-field leaves an array-shaped value as a numeric-keyed object
 * (e.g. { "0": {...} } or {}), which would otherwise make a later nested
 * `change("rows[i].x")` throw "Cannot set a numeric property on an object".
 */
export const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    return Object.keys(value)
      .filter((key) => /^\d+$/.test(key))
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => value[key]);
  }
  return [];
};

export const emptyTier = () => ({ threshold: "", value: "" });
export const emptyTimeTier = () => ({
  threshold: "",
  threshold_unit: DEFAULT_TIME_THRESHOLD_UNIT,
  value: "",
});
export const emptyVipRange = () => ({ start: "", end: "" });
export const emptyWeekdayRow = (label) => ({ label, ranges: [], markup: "" });
export const emptyVipRow = () => ({ label: "", ranges: [], markup: "" });

/** Append a row. Returns a new array (never mutates input). */
export const addRow = (rows, rowBuilder) => [
  ...asArray(rows),
  rowBuilder ? rowBuilder() : {},
];

/** Remove row at index. Returns a new array; ignores out-of-range indexes. */
export const removeRowAt = (rows, index) =>
  asArray(rows).filter((_, i) => i !== index);

/** Patch one row by index. Returns a new array. */
export const patchRowAt = (rows, index, patch) =>
  asArray(rows).map((row, i) => (i === index ? { ...row, ...patch } : row));

/** Append a time range to a VIP row. */
export const addRangeAt = (rows, index) =>
  patchRowAt(rows, index, {
    ranges: [...asArray(rows[index]?.ranges), emptyVipRange()],
  });

/** Remove a time range from a VIP row. */
export const removeRangeAt = (rows, index, rangeIndex) =>
  patchRowAt(rows, index, {
    ranges: asArray(rows[index]?.ranges).filter((_, i) => i !== rangeIndex),
  });

/**
 * Copy one VIP row's ranges + markup onto the target indexes.
 * Deep-copies ranges so targets never share references with the source.
 */
export const copyVipRowTo = (rows, sourceIndex, targetIndexes) => {
  const source = asArray(rows)[sourceIndex];
  if (!source) return asArray(rows);
  const targets = asArray(targetIndexes);
  return asArray(rows).map((row, i) =>
    targets.includes(i)
      ? {
          ...row,
          ranges: asArray(source.ranges).map((r) => ({ ...r })),
          markup: source.markup,
        }
      : row
  );
};

/**
 * Normalize rows for submission: drop empty VIP rows (no ranges) and trim
 * whitespace; returns valid, serializable data.
 */
export const normalizeTiers = (rows) =>
  asArray(rows).map((t) => ({
    threshold: t?.threshold === "" ? "" : Number(t?.threshold),
    threshold_unit: t?.threshold_unit,
    value: t?.value === "" ? "" : Number(t?.value),
  }));

export const normalizeVipRows = (rows) =>
  asArray(rows)
    .filter((r) => asArray(r?.ranges).length > 0)
    .map((r) => ({
      label: (r?.label || "").trim(),
      ranges: asArray(r?.ranges).map((x) => ({
        start: (x?.start || "").trim(),
        end: (x?.end || "").trim(),
      })),
      markup: r?.markup === "" ? "" : Number(r?.markup),
    }));
